import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Back, Footer, SubmitButton } from '..';

import { QuizContext } from '../../../store/quiz';
import { media } from '../../../utils/styles';
import { FormGroup } from '../FormGroup';

export const kgToLbs = kg => {
  const lbs = kg * 2.2046;
  return Math.round((lbs + Number.EPSILON) * 10) / 10;
};

const lbsToKg = lbs => {
  const kg = lbs / 2.2046;
  return Math.round((kg + Number.EPSILON) * 10) / 10;
};

const cmToImperial = cm => {
  const feets = (cm * 0.3937) / 12;
  const roundedFeets = Math.floor(feets);
  const inches = Math.round((feets - roundedFeets) * 12);
  return [roundedFeets, inches];
};

const imperialToCm = ([ft, inches]) => {
  const cm = (ft * 12 + parseFloat(inches)) / 0.3937;
  return Math.round(cm);
};

export const WeightHeight = () => {
  const {
    store: { submitAnswers, prevStep, answers, quizLog },
  } = useContext(QuizContext);

  const [validationError, setValidationError] = useState(null);
  const [weight, setWeight] = useState(answers['weight'] || '');
  const [desiredWeight, setDesiredWeight] = useState(answers['weight'] || '');
  const [height, setHeight] = useState(answers['height'] || '');
  const [units, setUnits] = useState(answers['units'] || 'imperial');
  const [lbs, setLbs] = useState(null);
  const [desiredLbs, setDesiredLbs] = useState(null);
  const [ft, setFt] = useState(null);
  const [inches, setInches] = useState(null);

  useEffect(() => {
    if (answers['units'] === 'imperial') {
      setLbs(kgToLbs(answers['weight']));
      setDesiredLbs(kgToLbs(answers['desired_weight']));
      const [ft, inches] = cmToImperial(answers['height']);
      setFt(ft);
      setInches(inches);
    }
  }, [answers]);

  useEffect(() => {
    setUnits(window.__locale?.units || 'imperial');
  }, []);

  useEffect(() => {
    if (lbs && ft && inches && desiredLbs) {
      setWeight(lbsToKg(lbs));
      setDesiredWeight(lbsToKg(desiredLbs));
      setHeight(imperialToCm([ft, inches]));
    }
  }, [lbs, ft, inches]);

  const onUnitsChange = e => {
    e.preventDefault();
    if (units === 'metric') {
      if (weight) {
        setLbs(kgToLbs(weight));
      }
      if (desiredWeight) {
        setDesiredLbs(kgToLbs(desiredWeight));
      }
      if (height) {
        const [ft, inches] = cmToImperial(height);
        setFt(ft);
        setInches(inches);
      }
      return setUnits('imperial');
    }
    if (lbs) {
      setWeight(lbsToKg(lbs));
    }
    if (desiredLbs) {
      setDesiredWeight(lbsToKg(desiredLbs));
    }
    if (ft && inches) {
      setHeight(imperialToCm([ft, inches]));
    }
    setUnits('metric');
  };

  const validateForm = () => {
    if (parseFloat(desiredWeight) > parseFloat(weight)) {
      setValidationError('Desired weight cannot be greater than or equal to current weight!');
      return false;
    }
    return true;
  };

  const onSubmit = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const weightKg = units === 'imperial' ? lbsToKg(lbs) : weight;
    const desiredWeightKg = units === 'imperial' ? lbsToKg(desiredLbs) : desiredWeight;
    const heightCm = units === 'imperial' ? imperialToCm([ft, inches]) : height;
    submitAnswers([
      {
        name: 'weight',
        value: weightKg,
      },
      {
        name: 'desired_weight',
        value: desiredWeightKg,
      },
      {
        name: 'height',
        value: heightCm,
      },
      {
        name: 'units',
        value: units,
      },
    ]);
    quizLog(`Answer_${weightKg}`, 'weight');
    quizLog(`Answer_${desiredWeightKg}`, 'desired_weight');
    quizLog(`Answer_${heightCm}`, 'height');
    quizLog(`Answer_${units}`, 'units');
  };

  return (
    <Wrap onSubmit={onSubmit}>
      <Heading>What are your height and weight?</Heading>
      <UnitsSwitch $left={units === 'imperial'} onClick={onUnitsChange}>
        <span>Imperial</span>
        <span>Metric</span>
      </UnitsSwitch>
      <Form>
        {units === 'imperial' ? (
          <>
            <FormGroup
              value={lbs}
              onChange={e => setLbs(e.target.value)}
              placeholder="Current weight"
              type="number"
              name="lbs"
              step="0.1"
              required
              min="1"
              max="440"
              label="lbs"
            />
            <FormGroup
              value={desiredLbs}
              onChange={e => setDesiredLbs(e.target.value)}
              placeholder="Desired weight"
              type="number"
              name="lbs"
              step="0.1"
              required
              min="1"
              max="440"
              label="lbs"
            />
            <Row>
              <FormGroup
                value={ft}
                onChange={e => setFt(e.target.value)}
                placeholder="Height"
                type="number"
                step="1"
                min="1"
                max="200"
                name="fts"
                label="ft"
                required
              />
              <FormGroup
                value={inches}
                onChange={e => setInches(e.target.value)}
                type="number"
                step="1"
                min="0"
                max="11"
                name="inches"
                label="in"
                required
              />
            </Row>
          </>
        ) : (
          <>
            <FormGroup
              value={weight}
              onChange={e => setWeight(e.target.value)}
              placeholder="Current weight"
              type="number"
              name="weight"
              step="0.01"
              required
              min="30"
              max="200"
              label="kg"
            />
            <FormGroup
              value={desiredWeight}
              onChange={e => setDesiredWeight(e.target.value)}
              placeholder="Desired weight"
              type="number"
              name="desired_weight"
              step="0.01"
              required
              min="30"
              max="200"
              label="kg"
            />
            <FormGroup
              value={height}
              onChange={e => setHeight(e.target.value)}
              placeholder="Height"
              type="number"
              step="1"
              min="100"
              max="250"
              name="height"
              label="cm"
              required
            />
          </>
        )}
      </Form>
      {validationError && <Alert>{validationError}</Alert>}
      <Footer>
        <div>
          <SubmitButton type="submit" disabled={!weight || !height || !desiredWeight}>
            Continue
          </SubmitButton>
        </div>
        <Back onBack={prevStep} />
      </Footer>
    </Wrap>
  );
};

const Alert = styled.div`
  background: rgba(221, 48, 114, 0.08);
  border-radius: 8px;
  width: 374px;
  max-width: 100%;
  margin: 20px auto 0;
  padding: 12px;
  font-weight: 500;
  font-size: 14px;
  line-height: 155%;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
`;

const UnitsSwitch = styled.button`
  display: flex;
  width: 242px;
  height: 44px;
  align-items: center;
  position: relative;
  border-radius: 22px;
  background: #e9e9e9;
  margin: 0 auto 36px;
  padding: 0;
  > span {
    font-size: 14px;
    width: 50%;
    color: #222222;
    text-align: center;
    position: relative;
    z-index: 2;
  }
  &::before {
    position: absolute;
    height: 40px;
    width: calc(50% - 2px);
    background: #ffffff;
    border-radius: 22px;
    content: '';
    left: ${({ $left }) => ($left ? '2px' : 'calc(50%)')};
    transition: left 0.15s ease-out;
    top: 2px;
  }
`;

const Form = styled.div`
  width: 320px;
  max-width: 100%;
  margin: 0 auto;
`;

const Heading = styled.h1`
  width: 572px;
  max-width: 100%;
  font-weight: bold;
  font-size: 32px;
  margin: 0 auto 40px;
  line-height: 43px;
  text-transform: capitalize;
  ${media.phone} {
    margin: 0 0 24px;
    font-size: 28px;
    line-height: 36px;
  }
`;

const Wrap = styled.form`
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
